import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../apiClient';
import { Profile } from '../../hooks/useAuth';
import { UserPlus, Link as LinkIcon, Trash2, CheckCircle2, X, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { FRONTEND_URL } from '../../config';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';

interface Invitation {
  id: string;
  email: string;
  role: string;
  token: string;
  status: string;
  created_at: string;
  expires_at: string;
}

export const InvitationManagement: React.FC<{ profile: Profile }> = () => {
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/admin/invitations');
      if (!data.success) throw new Error(data.message);
      setInvites(data.invitations || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvites(); }, []);

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      const data = await apiFetch('/admin/invitations', {
        method: 'POST',
        body: JSON.stringify({ email, role: 'provider' }),
      });
      if (!data.success) throw new Error(data.message);
      setSuccess(`Invitation for ${email} created successfully`);
      setEmail('');
      setIsModalOpen(false);
      fetchInvites();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteInvite = async (id: string) => {
    if (!window.confirm('Delete this invitation?')) return;
    try {
      await apiFetch(`/admin/invitations/${id}`, { method: 'DELETE' });
      fetchInvites();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const copyInviteLink = (token: string, inviteEmail: string) => {
    const link = `${FRONTEND_URL}/invite/${token}`;
    navigator.clipboard.writeText(link);
    alert(`Invite link for ${inviteEmail} copied to clipboard!`);
  };

  if (loading && invites.length === 0) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading invitations...</p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h2 className="display text-2xl md:text-3xl text-ink">Invitations</h2>
          <p className="label-caps text-ink-faint mt-2">Control who can join NexSlot</p>
        </div>
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
          <Plus size={16} /> Invite provider
        </Button>
      </div>

      {success && (
        <div className="p-4 flex items-center gap-3 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-sm border border-green-200 dark:border-green-900 text-xs font-semibold">
          <CheckCircle2 size={16} className="shrink-0" /> {success}
        </div>
      )}
      {error && (
        <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
          <X size={16} className="shrink-0" /> {error}
        </div>
      )}

      {invites.length === 0 ? (
        <EmptyState
          icon={UserPlus}
          title="No active invitations"
          hint="Invite your first provider to get started"
          action={<Button variant="primary" onClick={() => setIsModalOpen(true)}><Plus size={16} /> Invite provider</Button>}
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-left min-w-[640px]">
            <thead className="bg-inverse">
              <tr>
                <th className="px-6 py-4 label-caps text-slate-400">Email</th>
                <th className="px-6 py-4 label-caps text-slate-400">Status</th>
                <th className="px-6 py-4 label-caps text-slate-400">Created</th>
                <th className="px-6 py-4 label-caps text-slate-400">Link</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {invites.map((invite) => (
                <tr key={invite.id} className="hover:bg-surface transition-colors">
                  <td className="px-6 py-5 text-sm font-semibold text-ink">{invite.email}</td>
                  <td className="px-6 py-5">
                    {invite.status === 'pending'
                      ? <Badge status="pending">{invite.status}</Badge>
                      : <Badge status="completed">{invite.status}</Badge>}
                  </td>
                  <td className="px-6 py-5 text-xs text-ink-muted">{format(new Date(invite.created_at), 'do MMM yyyy')}</td>
                  <td className="px-6 py-5">
                    <button onClick={() => copyInviteLink(invite.token, invite.email)}
                      className="flex items-center gap-2 text-accent label-caps hover:underline">
                      <LinkIcon size={14} /> Copy link
                    </button>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <button onClick={() => deleteInvite(invite.id)}
                      className="p-2 text-ink-faint hover:text-red-500 hover:bg-surface rounded-sm transition-colors"
                      aria-label="Delete invitation">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-raised w-full max-w-md rounded-sm border border-line overflow-hidden">
            <div className="bg-inverse px-6 md:px-8 py-5 flex items-center justify-between">
              <h3 className="display text-lg text-ink-inverse">Invite new provider</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" aria-label="Close">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleCreateInvite} className="p-6 md:p-8 space-y-5">
              <Input
                required
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="provider@example.com"
              />
              <div className="flex gap-3">
                <Button type="button" variant="ghost" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" className="flex-1">
                  Create invite
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
